<!--
  The one rendering of an availability decision (spec DSP-03): the driver
  picker, the truck picker and the board's filter all show the server's
  answer through this, so "why can't I pick them" reads the same everywhere.
  Green for available, amber for needs_review / unknown (with the reasons),
  red for blocked. Never hides a reason: the spec says show why a driver
  cannot be selected, not hide the driver.
-->
<template>
  <LwNotice v-if="loading" tone="info" class="lw-c-availability"> Checking availability… </LwNotice>
  <LwNotice v-else-if="decision" :tone="tone" :title="title" class="lw-c-availability">
    <ul v-if="decision.reasons && decision.reasons.length" class="lw-c-availability__reasons">
      <li v-for="(reason, index) in decision.reasons" :key="index">
        <strong>{{ label(reason.code) }}.</strong> {{ reason.message }}
      </li>
    </ul>
    <p v-else class="lw-c-availability__ok">
      No schedule gap, leave, other load or equipment problem overlaps this commitment.
    </p>
    <p v-if="decision.next_windows && decision.next_windows.length" class="lw-c-availability__next">
      Next working hours: {{ nextWindows }}
    </p>
    <p v-if="overrideHint" class="lw-c-availability__hint">{{ overrideHint }}</p>
  </LwNotice>
</template>

<script>
import LwNotice from '../lw/LwNotice.vue';
import {
  decisionTone,
  decisionTitle,
  reasonLabel,
  fmtRange,
} from '../../pages/Scheduling/schedulingFormat';

export default {
  name: 'AvailabilityNotice',
  components: { LwNotice },
  props: {
    // GET /scheduling/availability's body, or null before a check.
    decision: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    // The zone times are printed in (the org's).
    zone: {
      type: String,
      required: true,
    },
    // Whether the viewer may override with a reason (scheduling.manage).
    canOverride: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    tone() {
      return decisionTone(this.decision && this.decision.status);
    },
    title() {
      return decisionTitle(this.decision && this.decision.status);
    },
    nextWindows() {
      return (this.decision.next_windows || [])
        .slice(0, 2)
        .map(w => fmtRange(w.starts_at, w.ends_at, this.zone))
        .join(', ');
    },
    overrideHint() {
      if (!this.decision || this.decision.status === 'available') return null;
      const override = this.decision.override || {};
      const hard = override.with_reason || [];
      if (!this.canOverride) {
        return 'Assigning anyway needs someone with scheduling rights and a reason.';
      }
      if (hard.length) {
        return `Cannot be assigned even with a reason: ${hard.map(reasonLabel).join(', ')}.`;
      }
      return 'Type a reason below to assign anyway; it is recorded on the load.';
    },
  },
  methods: {
    label: reasonLabel,
  },
};
</script>

<style scoped lang="scss">
.lw-c-availability__reasons {
  margin: 0;
  padding-left: var(--lw-space-4);
  display: flex;
  flex-direction: column;
  gap: var(--lw-space-1);
}

.lw-c-availability__ok,
.lw-c-availability__next,
.lw-c-availability__hint {
  margin: var(--lw-space-2) 0 0;
  font: var(--lw-type-body-sm);
}
</style>
